import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useReseedMarketPosterior } from '../../api/hooks';
import {
  logitProb,
  reseedConfirmBody,
  reseedErrorMessage,
  reseedInputsFromQuote,
  reseedPreview as buildReseedPreview,
  reseedSqrtPGateWarning,
  reseedValidationError,
  sigmoidLogit,
} from '../../utils/reseedPosterior';
import { marketLabel } from './tradingDashUtils';
import '../../components/HomebaseModal.css';
import './ReseedPosteriorModal.css';

const EMPTY_INPUTS = { line: '', prob: '', x: '', P: '' };

/**
 * Hard-reseed a market's baseline line and posterior (x, P) from the Overview board.
 *
 * Same endpoint and same validation as the dashboard's ManualGatingPanel — both go through
 * ``utils/reseedPosterior`` — but rendered as a modal so the dense tile grid stays readable.
 * ``confirm`` / ``alert`` are passed in from the page's HomebaseModal so this dialog and the
 * confirmation stack in the same layer.
 */
export default function ReseedPosteriorModal({ quote, onClose, confirm, alert, actedBy }) {
  const marketKey = quote?.market_key || null;
  const [inputs, setInputs] = useState(EMPTY_INPUTS);
  const [lockBaseline, setLockBaseline] = useState(true);
  const reseedPosterior = useReseedMarketPosterior();

  const loadFromQuote = useCallback(() => {
    setInputs(reseedInputsFromQuote(quote) || EMPTY_INPUTS);
    setLockBaseline(true);
  }, [quote]);

  // Seed once per market so typing isn't clobbered by quote refetches.
  useEffect(() => {
    loadFromQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketKey]);

  const preview = useMemo(() => buildReseedPreview(inputs), [inputs]);
  const invalid = reseedValidationError(preview);
  const busy = reseedPosterior.isPending;

  const setField = (key, value) => setInputs((prev) => ({ ...prev, [key]: value }));

  /** Keep prob and x in step, so whichever field the operator edits is the one that wins. */
  const onProbChange = (value) => {
    const p = Number.parseFloat(String(value).trim());
    setInputs((prev) => ({
      ...prev,
      prob: value,
      x: Number.isFinite(p) && p > 0 && p < 1 ? logitProb(p).toFixed(4) : prev.x,
    }));
  };
  const onXChange = (value) => {
    const x = Number.parseFloat(String(value).trim());
    setInputs((prev) => ({
      ...prev,
      x: value,
      prob: Number.isFinite(x) ? sigmoidLogit(x).toFixed(4) : prev.prob,
    }));
  };

  const handleSubmit = async () => {
    if (invalid) {
      await alert({ title: 'Reseed', body: invalid, warn: true });
      return;
    }
    const ok = await confirm({
      title: 'Reseed posterior',
      body: `${marketKey}\n\n${reseedConfirmBody(preview, { lockBaseline })}`,
      danger: true,
      confirmLabel: 'Reseed',
    });
    if (!ok) return;
    try {
      const res = await reseedPosterior.mutateAsync({
        marketKey,
        line: preview.line,
        P: preview.P,
        x: preview.x,
        lockBaseline,
        actedBy,
      });
      const sqrtPWarning = reseedSqrtPGateWarning(res);
      if (sqrtPWarning) {
        await alert({ title: 'Reseeded (√P gate)', body: sqrtPWarning, warn: true });
      }
      onClose();
    } catch (err) {
      console.error('Failed to reseed posterior:', err);
      await alert({ title: 'Request failed', body: reseedErrorMessage(err), warn: true });
    }
  };

  if (!quote) return null;

  return (
    <div
      className="hb-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="hb-modal tdash-reseed" role="dialog" aria-modal="true" aria-label="Reseed posterior">
        <h4>Reseed posterior</h4>
        <div className="tdash-reseed-market">
          <span className="tdash-reseed-player">{quote.player_name || quote.market_key}</span>
          <span className="tdash-reseed-mkt">
            {marketLabel(quote.market_id)} · current line {quote.line}
          </span>
        </div>

        <div className="tdash-reseed-grid">
          <label>
            <span>Line</span>
            <input
              type="text"
              inputMode="decimal"
              value={inputs.line}
              onChange={(e) => setField('line', e.target.value)}
              disabled={busy}
              placeholder="e.g. 11.25"
            />
          </label>
          <label>
            <span>P (logit variance)</span>
            <input
              type="text"
              inputMode="decimal"
              value={inputs.P}
              onChange={(e) => setField('P', e.target.value)}
              disabled={busy}
              placeholder="e.g. 0.0400"
            />
          </label>
          <label>
            <span>Fair p</span>
            <input
              type="text"
              inputMode="decimal"
              value={inputs.prob}
              onChange={(e) => onProbChange(e.target.value)}
              disabled={busy}
              placeholder="0–1"
            />
          </label>
          <label>
            <span>x (logit)</span>
            <input
              type="text"
              inputMode="decimal"
              value={inputs.x}
              onChange={(e) => onXChange(e.target.value)}
              disabled={busy}
              placeholder="logit of fair"
            />
          </label>
        </div>

        <div className="tdash-reseed-preview">
          {preview.p != null ? (
            <span>
              fair <strong>{(preview.p * 100).toFixed(1)}%</strong>
              {preview.x != null ? <span className="tdash-reseed-dim"> · x {preview.x.toFixed(4)}</span> : null}
            </span>
          ) : (
            <span className="tdash-reseed-dim">Enter fair p or x</span>
          )}
          {preview.sqrtP != null ? (
            <span>
              √P <strong>{preview.sqrtP.toFixed(3)}</strong>
              {preview.sdProb ? (
                <span className="tdash-reseed-dim">
                  {' '}
                  · ~90% CI {(preview.sdProb.lo * 100).toFixed(1)}–{(preview.sdProb.hi * 100).toFixed(1)}%
                </span>
              ) : null}
            </span>
          ) : null}
        </div>

        <label className="tdash-reseed-lock">
          <input
            type="checkbox"
            checked={lockBaseline}
            onChange={(e) => setLockBaseline(e.target.checked)}
            disabled={busy}
          />
          <span>Lock baseline to this line (stops auto line-shift undoing the reseed)</span>
        </label>

        <p className="hb-modal-body hb-modal-body--warn tdash-reseed-warn">
          Hard reseed: overwrites the posterior, clears jump excess, and forces a full order-book rebuild.
        </p>

        <div className="hb-modal-actions">
          <button type="button" className="btn-hb-modal-cancel" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-hb-modal-cancel" onClick={loadFromQuote} disabled={busy}>
            Reload from quote
          </button>
          <button
            type="button"
            className="btn-hb-modal-danger"
            onClick={handleSubmit}
            disabled={busy || Boolean(invalid)}
            title={invalid || 'Reseed this market'}
          >
            {busy ? 'Reseeding…' : 'Reseed'}
          </button>
        </div>
      </div>
    </div>
  );
}
